import { buildSearchText, formatCurrency, toDate } from "@nomnom/core";
import type { Order, RiderProfile, User } from "@nomnom/core";
import {
  collection,
  limit,
  onSnapshot,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../config/firebase";
import type { ConfigFormState } from "./configService";

type HallMap = Record<string, string>;
type UserMap = Record<string, User>;

export type DashboardOrderRow = {
  id: string;
  data: Order;
};

export type DashboardSnapshot = {
  orders: DashboardOrderRow[];
  users: User[];
  riders: RiderProfile[];
  diningHallNames: HallMap;
};

export type DashboardStat = {
  title: string;
  value: string;
  subtitle: string;
  color: string;
};

export type LaunchNightMetrics = {
  activeOrders: number;
  searchingOrders: number;
  onlineRiders: number;
  completedToday: number;
  capRemaining: number | null;
  readyToOpen: boolean;
  pressure: "clear" | "watch" | "pause";
  warnings: string[];
};

export function subscribeToDashboardOrders(
  onUpdate: (orders: DashboardOrderRow[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    query(collection(db, "orders"), orderBy("createdAt", "desc"), limit(200)),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map((docSnap) => ({
          id: docSnap.id,
          data: { id: docSnap.id, ...docSnap.data() } as Order,
        })),
      );
    },
    (error) => {
      console.error("[DashboardService] Failed to load orders:", error);
      onError("Failed to load dashboard orders.");
    },
  );
}

export function subscribeToDashboardUsers(
  onUpdate: (users: User[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "users"),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map(
          (docSnap) => ({ uid: docSnap.id, ...docSnap.data() }) as User,
        ),
      );
    },
    (error) => {
      console.error("[DashboardService] Failed to load users:", error);
      onError("Failed to load dashboard users.");
    },
  );
}

export function subscribeToDashboardRiders(
  onUpdate: (riders: RiderProfile[]) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "riderProfiles"),
    (snapshot) => {
      onUpdate(
        snapshot.docs.map(
          (docSnap) => ({ uid: docSnap.id, ...docSnap.data() }) as RiderProfile,
        ),
      );
    },
    (error) => {
      console.error("[DashboardService] Failed to load riders:", error);
      onError("Failed to load dashboard riders.");
    },
  );
}

export function subscribeToDiningHallNames(
  onUpdate: (diningHallNames: HallMap) => void,
  onError: (message: string) => void,
): () => void {
  return onSnapshot(
    collection(db, "diningHalls"),
    (snapshot) => {
      const hallMap: HallMap = {};
      snapshot.docs.forEach((docSnap) => {
        const data = docSnap.data() as { name?: string };
        hallMap[docSnap.id] = data.name || docSnap.id;
      });
      onUpdate(hallMap);
    },
    (error) => {
      console.error("[DashboardService] Failed to load dining halls:", error);
      onError("Failed to load dining hall metadata.");
    },
  );
}

export function buildUsersById(users: User[]): UserMap {
  return users.reduce<UserMap>((accumulator, user) => {
    accumulator[user.uid] = user;
    return accumulator;
  }, {});
}

function isSameDay(candidate: Date, reference: Date): boolean {
  return (
    candidate.getFullYear() === reference.getFullYear() &&
    candidate.getMonth() === reference.getMonth() &&
    candidate.getDate() === reference.getDate()
  );
}

function formatDelta(current: number, previous: number, suffix = ""): string {
  if (previous === 0) {
    if (current === 0) {
      return "No change";
    }

    return `+${current}${suffix} vs yesterday`;
  }

  const rawPercent = ((current - previous) / previous) * 100;
  const roundedPercent = Math.round(rawPercent);
  const direction = roundedPercent >= 0 ? "+" : "";
  return `${direction}${roundedPercent}% vs yesterday`;
}

export function buildDashboardMetrics(
  orders: DashboardOrderRow[],
  users: User[],
  riders: RiderProfile[],
): { stats: DashboardStat[]; recentOrders: DashboardOrderRow[] } {
  const now = new Date();
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);

  const todayOrders = orders.filter((order) => {
    const createdAt = toDate(order.data.createdAt);
    return createdAt ? isSameDay(createdAt, now) : false;
  });
  const yesterdayOrders = orders.filter((order) => {
    const createdAt = toDate(order.data.createdAt);
    return createdAt ? isSameDay(createdAt, yesterday) : false;
  });

  const todayRevenue = todayOrders.reduce((total, order) => {
    const orderTotal =
      typeof order.data.total === "number"
        ? order.data.total
        : (order.data.fee || 0) + (order.data.tip || 0);
    return total + orderTotal;
  }, 0);

  const yesterdayRevenue = yesterdayOrders.reduce((total, order) => {
    const orderTotal =
      typeof order.data.total === "number"
        ? order.data.total
        : (order.data.fee || 0) + (order.data.tip || 0);
    return total + orderTotal;
  }, 0);

  const activeRiders = riders.filter((rider) => rider.online).length;
  const totalStudents = users.filter((user) => user.roles?.student).length;
  const todayStudents = users.filter((user) => {
    if (!user.roles?.student) {
      return false;
    }
    const createdAt = toDate(user.createdAt);
    return createdAt ? isSameDay(createdAt, now) : false;
  }).length;

  return {
    stats: [
      {
        title: "Today's Orders",
        value: String(todayOrders.length),
        subtitle: formatDelta(todayOrders.length, yesterdayOrders.length),
        color: "#1e40af",
      },
      {
        title: "Active Riders",
        value: String(activeRiders),
        subtitle: `${riders.length} total riders`,
        color: "#059669",
      },
      {
        title: "Total Students",
        value: totalStudents.toLocaleString(),
        subtitle: `${todayStudents} joined today`,
        color: "#dc2626",
      },
      {
        title: "Revenue Today",
        value: formatCurrency(todayRevenue),
        subtitle: formatDelta(todayRevenue, yesterdayRevenue),
        color: "#7c3aed",
      },
    ],
    recentOrders: orders
      .filter((order) =>
        Boolean(buildSearchText([order.id, order.data.customerUid, order.data.riderUid])),
      )
      .slice(0, 8),
  };
}

export function buildLaunchNightMetrics(
  orders: DashboardOrderRow[],
  riders: RiderProfile[],
  config: Pick<
    ConfigFormState,
    | "orderingEnabled"
    | "launchNightOrderCap"
    | "minimumOnlineRiders"
    | "maxSearchingOrders"
  >,
): LaunchNightMetrics {
  const now = new Date();
  const todayOrders = orders.filter((order) => {
    const createdAt = toDate(order.data.createdAt);
    return createdAt ? isSameDay(createdAt, now) : false;
  });
  const openOrders = orders.filter(
    (order) => !["delivered", "cancelled"].includes(order.data.status),
  );
  const activeOrders = openOrders.length;
  const searchingOrders = openOrders.filter(
    (order) => order.data.status === "searching",
  ).length;
  const completedToday = todayOrders.filter(
    (order) => order.data.status === "delivered",
  ).length;
  const onlineRiders = riders.filter((rider) => rider.online).length;
  const capRemaining =
    config.launchNightOrderCap > 0
      ? Math.max(config.launchNightOrderCap - todayOrders.length, 0)
      : null;
  const warnings: string[] = [];

  if (!config.orderingEnabled) {
    warnings.push("Ordering is paused from admin.");
  }

  if (onlineRiders < config.minimumOnlineRiders) {
    warnings.push(
      `${config.minimumOnlineRiders - onlineRiders} more rider(s) needed before opening.`,
    );
  }

  if (searchingOrders > config.maxSearchingOrders) {
    warnings.push("Searching backlog is above the configured pause threshold.");
  }

  if (capRemaining === 0) {
    warnings.push("Nightly order cap has been reached.");
  }

  const pressure =
    searchingOrders > config.maxSearchingOrders || capRemaining === 0
      ? "pause"
      : searchingOrders > 0 || onlineRiders === config.minimumOnlineRiders
        ? "watch"
        : "clear";

  return {
    activeOrders,
    searchingOrders,
    onlineRiders,
    completedToday,
    capRemaining,
    readyToOpen:
      config.orderingEnabled &&
      onlineRiders >= config.minimumOnlineRiders &&
      searchingOrders <= config.maxSearchingOrders &&
      capRemaining !== 0,
    pressure,
    warnings,
  };
}
